import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Form, 
  Button, 
  Space, 
  message, 
  Typography, 
  Avatar,
  Row,
  Col
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import PageLayout from '../../components/layout/PageLayout';
import BannerForm from '../../components/banners/BannerForm';
import ImageUpload from '../../components/banners/ImageUpload';
import bannerService from '../../services/bannerService';
import masterService from '../../services/masterService';
import { useUser } from '../../context/UserContext';

const { Title, Text } = Typography;

const AddBanner = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [form] = Form.useForm();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    m_banner_type: undefined,
    show_type: undefined
  });
  
  // Dropdown data
  const [countries, setCountries] = useState([]);
  const [salesChannels, setSalesChannels] = useState([]);
  const [videoShows, setVideoShows] = useState([]);
  const [tvShows, setTvShows] = useState([]);
  const [languages, setLanguages] = useState([]);

  // Load dropdown data
  useEffect(() => {
    loadDropdownData();
  }, []);

  const loadDropdownData = async () => {
    try {
      setLoading(true);
      
      // Load all dropdown data in parallel
      const [
        countriesRes,
        salesChannelsRes,
        videoShowsRes,
        tvShowsRes,
        languagesRes
      ] = await Promise.allSettled([
        masterService.getCountries(),
        masterService.getSalesChannels(),
        masterService.getVideoShows(),
        masterService.getTvShows(),
        masterService.getLanguages()
      ]);

      // Set countries
      if (countriesRes.status === 'fulfilled' && countriesRes.value.data?.status) {
        setCountries(countriesRes.value.data.data || []);
      }

      // Set sales channels
      if (salesChannelsRes.status === 'fulfilled' && salesChannelsRes.value.data?.status) {
        setSalesChannels(salesChannelsRes.value.data.data || []);
      }

      // Set video shows
      if (videoShowsRes.status === 'fulfilled' && videoShowsRes.value.data?.status) {
        setVideoShows(videoShowsRes.value.data.data || []);
      }

      // Set TV shows
      if (tvShowsRes.status === 'fulfilled' && tvShowsRes.value.data?.status) {
        setTvShows(tvShowsRes.value.data.data || []);
      }

      // Set languages
      if (languagesRes.status === 'fulfilled' && languagesRes.value.data?.status) {
        setLanguages(languagesRes.value.data.data || []);
      }

    } catch (error) {
      console.error('Error loading dropdown data:', error);
      message.error('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  // Handle banner type change
  const handleBannerTypeChange = (value) => {
    setFormData(prev => ({ 
      ...prev, 
      m_banner_type: value,
      show_type: undefined // Reset show type when banner type changes
    }));
    
    // Reset show-related fields
    form.setFieldsValue({
      show_type: undefined,
      show_id: undefined,
      lang_id: undefined
    });
  };

  // Handle show type change
  const handleShowTypeChange = (value) => {
    setFormData(prev => ({ 
      ...prev, 
      show_type: value 
    }));
    
    // Reset show selection when show type changes
    form.setFieldsValue({
      show_id: undefined
    });
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      const response = await bannerService.addBanner(values);
      
      if (response.data && response.data.status) {
        message.success(response.data.message || 'Banner added successfully!');
        form.resetFields();
        setFormData({
          m_banner_type: undefined,
          show_type: undefined
        });
        navigate('/banners/banner');
      } else {
        message.error(response.data.message || 'Failed to add banner');
      }
    } catch (error) {
      console.error('Error adding banner:', error);
      message.error('Failed to add banner');
    } finally {
      setLoading(false);
    }
  };

  // Handle form reset
  const handleReset = () => {
    form.resetFields();
    setFormData({
      m_banner_type: undefined,
      show_type: undefined
    });
  };

  // Handle go back
  const handleGoBack = () => {
    navigate(-1);
  };

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Add New Banner</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Add Banner"
      desc="Create a new banner for your OTT platform"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            home_page: 'Y',
            movie_page: 'Y',
            series_page: 'Y',
            content_type: 1,
            sales_channel: []
          }}
        >
          <BannerForm
            form={formData}
            countries={countries}
            salesChannels={salesChannels}
            videoShows={videoShows}
            tvShows={tvShows}
            languages={languages}
            onBannerTypeChange={handleBannerTypeChange}
            onShowTypeChange={handleShowTypeChange}
          >
            {/* Image Upload Components */}
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <ImageUpload
                  label="Upload Desktop Banner Image"
                  name="m_banner_image_url_1"
                  width={1280}
                  height={720}
                  imageType="banners"
                  required={true}
                />
              </Col>
              <Col span={12}>
                <ImageUpload
                  label="Upload Mobile Banner Image"
                  name="m_banner_image_url_2"
                  width={300}
                  height={400}
                  imageType="banners"
                  required={true}
                />
              </Col>
            </Row>

            <ImageUpload
              label="Airtel Feature Banner Image"
              name="airtel_feature_image"
              width={1920}
              height={548}
              imageType="video"
              required={false}
            />
          </BannerForm>

          {/* Form Actions */}
          <Form.Item style={{ marginTop: '32px' }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
                size="large"
              >
                Add Banner
              </Button>
              
              <Button
                icon={<ReloadOutlined />}
                onClick={handleReset}
                size="large"
              >
                Reset
              </Button>
              
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={handleGoBack}
                size="large"
              >
                Go Back
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </PageLayout>
  );
};

export default AddBanner;